import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuthStore } from './store/authStore';
import { supabase } from './lib/supabase/client';
import OnboardingFlow from './components/onboarding/OnboardingFlow';
import LoginPage from './pages/auth/LoginPage';
import SignupPage from './pages/auth/SignupPage';
import Dashboard from './components/Dashboard';
import Header from './components/Header';
import Hero from './components/Hero';
import Features from './components/Features';
import HowItWorks from './components/HowItWorks';
import GetStarted from './components/GetStarted';
import Changelog from './components/Changelog';
import ContactManagerLanding from './pages/contact-manager/ContactManagerLanding';
import ContactManagerHome from './pages/contact-manager/ContactManagerHome';
import ContactManagerLogin from './pages/contact-manager/ContactManagerLogin';
import ContactManagerSignup from './pages/contact-manager/ContactManagerSignup';
import ContactManagerSetup from './pages/contact-manager/ContactManagerSetup';
import CommandCenterWorkspace from './components/workspace/CommandCenterWorkspace';
import DevelopmentWorkspace from './components/workspace/DevelopmentWorkspace';
import OperationsWorkspace from './components/workspace/OperationsWorkspace';
import SalesWorkspace from './components/workspace/SalesWorkspace';
import WorkshopWorkspace from './components/workspace/WorkshopWorkspace';
import Sidebar from './navigation/Sidebar';

function App() {
  const { isAuthenticated, setUser } = useAuthStore();
  const [isLoading, setIsLoading] = React.useState(true);
  const [showChangelog, setShowChangelog] = React.useState(false);

  useEffect(() => {
    const loadInitialSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          setUser({
            id: session.user.id,
            email: session.user.email!,
            name: session.user.user_metadata.full_name || session.user.email!.split('@')[0],
            createdAt: new Date(session.user.created_at),
            updatedAt: new Date(session.user.updated_at)
          });
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Session load error:', error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') {
        setUser(null);
      } else if (event === 'SIGNED_IN' && session) {
        setUser({
          id: session.user.id,
          email: session.user.email!,
          name: session.user.user_metadata.full_name || session.user.email!.split('@')[0],
          createdAt: new Date(session.user.created_at),
          updatedAt: new Date(session.user.updated_at)
        });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [setUser]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-white">
        <Routes>
          <Route path="/login" element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
          } />
          <Route path="/signup" element={
            isAuthenticated ? <Navigate to="/onboarding" replace /> : <SignupPage />
          } />
          <Route path="/onboarding" element={
            isAuthenticated ? <OnboardingFlow /> : <Navigate to="/login" replace />
          } />
          <Route path="/dashboard" element={
            isAuthenticated ? <Dashboard /> : <Navigate to="/login" replace />
          } />
          <Route path="/contact-manager" element={<ContactManagerLanding />} />
          <Route path="/contact-manager/home" element={
            <ContactManagerHome />
          } />
          <Route path="/contact-manager/login" element={
            isAuthenticated ? <Navigate to="/contact-manager/home" replace /> : <ContactManagerLogin />
          } />
          <Route path="/contact-manager/signup" element={
            isAuthenticated ? <Navigate to="/contact-manager/home" replace /> : <ContactManagerSignup />
          } />
          <Route path="/contact-manager/setup" element={
            !isAuthenticated ? <Navigate to="/contact-manager/login" replace /> : <ContactManagerSetup />
          } />
          <Route path="/contactmanager" element={<ContactManagerLanding />} />
          <Route path="/contactmanager/home" element={
            <ContactManagerHome />
          } />
          <Route path="/contactmanager/login" element={
            isAuthenticated ? <Navigate to="/contactmanager/home" replace /> : <ContactManagerLogin />
          } />
          <Route path="/contactmanager/signup" element={
            isAuthenticated ? <Navigate to="/contactmanager/home" replace /> : <ContactManagerSignup />
          } />
          <Route path="/contactmanager/setup" element={
            !isAuthenticated ? <Navigate to="/contactmanager/login" replace /> : <ContactManagerSetup />
          } />
          <Route path="/" element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <>
                <Header />
                <main>
                  <Hero />
                  <Features />
                  <HowItWorks />
                  <GetStarted />
                  <div className="text-center py-4 text-sm text-gray-500 space-y-1">
                    <div className="flex items-center justify-center space-x-4">
                     <span>v0.5.3</span>
                      <span>•</span>
                      <button onClick={() => setShowChangelog(true)} className="hover:text-blue-500">
                        View Changelog
                      </button>
                    </div>
                    <p>© 2024 <a href="http://thezillaproject.com" target="_blank" rel="noopener noreferrer" className="hover:text-blue-500">The Zilla Project</a> All Rights Reserved.</p>
                  </div>
                </main>
                {showChangelog && <Changelog onClose={() => setShowChangelog(false)} />}
              </>
            )
          } />
        </Routes>
      </div>
    </Router>
  );
}

export default App;